import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Modal } from 'antd';
import PropTypes from 'prop-types';
import { importTherapiesFromExcel } from '../../api/therapiesApi';

const stringifyResponse = (value) => {
  if (!value) return '';

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const getImportErrors = (data) => {
  if (Array.isArray(data?.errors)) return data.errors;
  if (Array.isArray(data?.data?.errors)) return data.data.errors;
  if (Array.isArray(data?.importErrors)) return data.importErrors;
  if (Array.isArray(data?.data?.importErrors)) return data.data.importErrors;
  return [];
};

const getCountSummary = (data) => {
  const source = data?.data && typeof data.data === 'object' ? data.data : data;
  const parts = [
    ['Kreirano', source?.createdCount],
    ['Azurirano', source?.updatedCount],
    ['Importovano', source?.importedCount],
    ['Preskoceno', source?.skippedCount],
  ]
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([label, value]) => `${label}: ${value}.`);

  return parts.join(' ');
};

const formatImportError = (item, index) => {
  if (typeof item === 'string') return item;

  const row = item?.rowNumber || item?.row || item?.line || '-';
  const identifier =
    item?.lbo || item?.userLbo || item?.therapyId || item?.patient || '';
  const message = item?.message || item?.error || stringifyResponse(item);

  return `Red ${row}${identifier ? `, ${identifier}` : ''}: ${message || `Greska ${index + 1}`}`;
};

const ImportTherapiesModal = ({ isOpen, onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [responseData, setResponseData] = useState(null);
  const [importErrors, setImportErrors] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    setFile(null);
    setLoading(false);
    setError('');
    setSuccessMessage('');
    setResponseData(null);
    setImportErrors([]);
  }, [isOpen]);

  const responseText = useMemo(
    () => stringifyResponse(responseData),
    [responseData]
  );

  const handleImport = useCallback(async (selectedFile) => {
    if (!selectedFile) {
      setError('Izaberite Excel fajl.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');
    setResponseData(null);
    setImportErrors([]);

    const response = await importTherapiesFromExcel(selectedFile);
    const errors = getImportErrors(response.data);

    setResponseData(response.data);
    setImportErrors(errors);

    if (!response.ok) {
      setError(response.message || 'Doslo je do greske pri importu terapija.');
      setLoading(false);
      return;
    }

    const countSummary = getCountSummary(response.data);
    setSuccessMessage(
      `${response.message || 'Terapije importovane.'}${countSummary ? ` ${countSummary}` : ''}`
    );
    onImported?.();

    setLoading(false);
  }, [onImported]);

  const handleCancel = () => {
    setFile(null);
    setLoading(false);
    setError('');
    setSuccessMessage('');
    setResponseData(null);
    setImportErrors([]);
    onClose();
  };

  return (
    <Modal
      className="create_user_modal"
      title="Import terapija iz Excel-a"
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
    >
      <div className="create_user_modal_form" autoComplete="off">
        <div>Izaberite Excel fajl sa terapijama. Podrzan format fajla je .xlsx.</div>
        <div>
          <label
            htmlFor="therapies-import-file"
            style={{ display: 'block', marginBottom: 6 }}
          >
            Excel fajl (.xlsx)
          </label>
          <input
            id="therapies-import-file"
            type="file"
            accept=".xlsx"
            disabled={loading}
            onChange={(event) => {
              const selectedFile = event.target.files?.[0] || null;
              setFile(selectedFile);
              setError('');
              setSuccessMessage('');
              setResponseData(null);
              setImportErrors([]);
              handleImport(selectedFile);
            }}
          />
          <div style={{ marginTop: 8, color: '#667085', fontSize: 13 }}>
            {file ? `Izabran fajl: ${file.name}` : 'Nijedan fajl nije izabran.'}
          </div>
        </div>
        {successMessage && (
          <Alert
            title={successMessage}
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        {error && (
          <Alert
            title={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        {!!importErrors.length && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            title={`Greske u importu (${importErrors.length})`}
            description={
              <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                {importErrors.map((item, index) => (
                  <div key={`${item?.rowNumber || item?.row || index}`}>
                    {formatImportError(item, index)}
                  </div>
                ))}
              </div>
            }
          />
        )}
        {responseText && (
          <Alert
            type={error ? 'error' : 'info'}
            showIcon
            style={{ marginBottom: 16 }}
            title="Response"
            description={
              <pre
                style={{
                  margin: 0,
                  maxHeight: 320,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {responseText}
              </pre>
            }
          />
        )}
      </div>
    </Modal>
  );
};

ImportTherapiesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onImported: PropTypes.func,
};

export default ImportTherapiesModal;
